import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RevenueCategoryProps } from "@/types/revenueCategory";
import { ApiDataProps } from "@/pages/Tenants";
import { DeleteBtn } from "@/components/Delete";
import { ShowBtn } from "@/components/Show";
import { UpdateRevenueCategoryBtn } from "./Update";
import {
  deleteRevenueCategoryData,
  getShowRevenueCategory,
} from "@/Api/revenueCategories";
import { formatDate } from "@/utils/format";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { MoreHorizontal } from "lucide-react";
import { CreateLoaderInput } from "@/components/Loaders";
import { TypePageActions } from "@/pages/Plans";

interface TableDataProps {
  dataApi: ApiDataProps<RevenueCategoryProps>;
  updateFront: React.Dispatch<
    React.SetStateAction<ApiDataProps<RevenueCategoryProps>>
  >;
  pageActions: TypePageActions;
}

export const TableData = ({
  dataApi,
  updateFront,
  pageActions,
}: TableDataProps) => {
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((category) => (
            <TableRow key={category.id}>
              <TableCell className="truncate max-w-80">
                {category.name}
              </TableCell>
              <TableCell>{category.description || "—"}</TableCell>
              <TableCell className="text-center">
                <div className="flex gap-3 w-full justify-center">
                  <UpdateRevenueCategoryBtn
                    uuid={category.uuid}
                    updateFront={updateFront}
                  />
                  <DeleteBtn
                    params={{
                      func: deleteRevenueCategoryData,
                      funcParams: category.uuid,
                    }}
                    updateFront={() => {
                      const updatedData = {
                        ...dataApi,
                        data: dataApi.data.filter(
                          (item) => item.uuid !== category.uuid
                        ),
                      };
                      updateFront(updatedData);
                    }}
                    referenceLabel={category.name}
                  />
                  <ShowBtn
                    labels={{ title: "Detalhes da Categoria" }}
                    params={{
                      func: getShowRevenueCategory,
                      funcParams: category.uuid,
                    }}
                    render={(data: RevenueCategoryProps) => (
                      <div className="flex flex-col gap-3">
                        <div>
                          <h3 className="text-sm font-medium">Nome:</h3>
                          <p>{data.name}</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium">Descrição:</h3>
                          <p>{data.description || "—"}</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium">
                            Data de Criação:
                          </h3>
                          <p>{formatDate(data.created_at)}</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium">
                            Última Atualização:
                          </h3>
                          <p>{formatDate(data.updated_at || "")}</p>
                        </div>
                      </div>
                    )}
                    loader={<CreateLoaderInput lines={4} />}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={8}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
